import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import {
  CreateConsumableRequest,
  ListConsumablesQuery,
  UpdateConsumableRequest,
} from './Consumables.types';

import {
  consumableActivateOrInactivate,
  createConsumable,
  deleteConsumable,
  getConsumable,
  listConsumables,
  updateConsumable,
} from './Consumables.service';

import Authorities from '@/types/Authority';
import { message, notification } from 'antd';
import { useIntl } from 'react-intl';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useConsumables(params?: ListConsumablesQuery) {
  const query = useInfiniteQuery(
    ['consumables', { ...params }],
    ({ pageParam }) => {
      const req: ListConsumablesQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listConsumables(req);
    },
    {
      enabled: hasPermissions([Authorities.REGISTER_CONSUMABLE_VIEW]),
      getNextPageParam: (lastPage) => lastPage.nextToken,
    },
  );

  useServiceError(query.error);

  return query;
}

export function useConsumable(id: number) {
  const query = useQuery(['consumables', id], () => getConsumable(id));

  useServiceError(query.error);

  return query;
}

export function useConsumableMutation() {
  const mutation = useMutation((consumableId: number) =>
    getConsumable(consumableId),
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCreateConsumable() {
  const queryChanel = useQueryChanel();

  const mutation = useMutation(
    (obj: CreateConsumableRequest) => createConsumable(obj),
    {
      async onSuccess() {
        notification.success({
          message: 'Sucesso',
          description: 'Insumo criado',
        });

        queryChanel({
          resetQueries: [['stockItems'], ['consumables']],
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateConsumable() {
  const intl = useIntl();
  const query = useQueryClient();
  const mutation = useMutation(
    (obj: UpdateConsumableRequest) => updateConsumable(obj),
    {
      onSuccess() {
        query.invalidateQueries(['consumables']);
        notification.success({
          message: intl.formatMessage({
            id: 'consumables.update.notifications.success.title',
          }),
          description: intl.formatMessage({
            id: 'consumables.update.notifications.success.description',
          }),
        });
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteConsumable() {
  const mutation = useMutation((id: number) => deleteConsumable(id), {
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('Consumable cannot be deleted due to relation.')
              ? 'Não é possivel excluir o insumo, pois ele tem vínculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveOrInactivateConsumable() {
  const intl = useIntl();

  const mutation = useMutation(
    (consumableId: number) => consumableActivateOrInactivate(consumableId),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({ id: 'consumables.status.message.success' }),
        );
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}
